import { MeshProperties, Mesh, MeshFields } from "@/types/core/Mesh";
import { Vector3 } from "three";
import * as THREE from "three";
import { Extrude } from "@react-three/drei";
import { forwardRef, useMemo } from "react";
import { TransformableItemProperties } from '../../core/Item';
import { Euler } from "three";
import { useAppContext } from "@/context/AppContext";
import { Material } from "@/types/core/Material";

export interface ExtrudeMeshProperties extends TransformableItemProperties {
  depth: number;
  bevelEnabled: boolean;
  bevelThickness: number;
  bevelSize: number;
  bevelOffset: number;
  bevelSegments: number;
  materialId?: string;
}

export interface ExtrudeMesh extends Mesh<ExtrudeMeshProperties> {
  subtype: 'extrude';
  properties: ExtrudeMeshProperties;
}

// Create a reusable Extrude component that can be used in both preview and world
const ExtrudeComponent = forwardRef<THREE.Mesh, { properties: ExtrudeMeshProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { depth, bevelEnabled, bevelThickness, bevelSize, bevelOffset, bevelSegments, transform, materialId } = properties;
  const { activeWorld } = useAppContext();
  
  // Create a simple shape to extrude
  const shape = new THREE.Shape();
  shape.moveTo(0, 0);
  shape.lineTo(0, 1);
  shape.lineTo(1, 1);
  shape.lineTo(1, 0);
  shape.lineTo(0, 0);
  
  // Extrude settings
  const extrudeSettings = {
    depth,
    bevelEnabled,
    bevelThickness,
    bevelSize,
    bevelOffset,
    bevelSegments
  };
  
  // In preview mode, use simplified transform
  const position: [number, number, number] = isPreview 
    ? [0, 0, 0] 
    : [transform.position.x, transform.position.y, transform.position.z];
  const rotation: [number, number, number] = isPreview
    ? [0, 0, 0]
    : [transform.rotation.x, transform.rotation.y, transform.rotation.z];
  const scale: [number, number, number] = isPreview
    ? [1, 1, 1]
    : [transform.scale.x, transform.scale.y, transform.scale.z];

  // Get the material from the world if available
  const materialItem = activeWorld && materialId ? activeWorld.items[materialId] : undefined;
  const material = materialItem?.type === 'material' ? (materialItem as unknown as Material) : undefined;

  // Create the material using the render function
  const meshMaterial = useMemo(() => {
    if (material) {
      return material.render(material.properties, isPreview);
    }
    return new THREE.MeshStandardMaterial({ color: "#808080" });
  }, [material, material?.properties, isPreview]);

  return (
    <Extrude
      ref={ref}
      args={[shape, extrudeSettings]}
      position={position}
      rotation={rotation}
      scale={scale}
      material={meshMaterial}
    />
  );
});

export const ExtrudeMeshItem: ExtrudeMesh = {
  id: '',
  name: 'Extrude',
  type: 'mesh',
  subtype: 'extrude',
  icon: 'Extrude',
  properties: {
    depth: 1,
    bevelEnabled: true,
    bevelThickness: 0.2,
    bevelSize: 0.1,
    bevelOffset: 0,
    bevelSegments: 3,
    materialId: '',
    transform: {
      position: new Vector3(),
      rotation: new Euler(),
      scale: new Vector3(1, 1, 1)
    }
  },
  fields: [
    ...MeshFields,
    {
      key: 'properties.depth',
      type: 'number',
      label: 'Depth',
      default: 1,
      panel: 'geometry'
    },
    {
      key: 'properties.bevelEnabled',
      type: 'boolean',
      label: 'Bevel Enabled',
      default: true,
      panel: 'geometry'
    },
    {
      key: 'properties.bevelThickness',
      type: 'number',
      label: 'Bevel Thickness',
      default: 0.2,
      panel: 'geometry'
    },
    {
      key: 'properties.bevelSize',
      type: 'number',
      label: 'Bevel Size',
      default: 0.1,
      panel: 'geometry'
    },
    {
      key: 'properties.bevelOffset',
      type: 'number',
      label: 'Bevel Offset',
      default: 0,
      panel: 'geometry'
    },
    {
      key: 'properties.bevelSegments',
      type: 'number',
      label: 'Bevel Segments',
      default: 3,
      panel: 'geometry'
    }
  ],
  render: (properties: ExtrudeMeshProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <ExtrudeComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.Mesh>} />;
  }
}; 